// Hand-written: the services accordion.
//
// Figma 6377:87365 (rows) and 6377:88138 (panels). **This section was blocked through
// all four waves of M3** on the grounds that its three nodes showed different designs
// that did not match the spec's "icon, title and body in a grid". Opening them settles
// it: neither is a grid, and the two are not two sections - they are the same expandable
// list in two layouts, so `layout` picks one.
//
// `rows` (6377:87365): full-width rows on hairline rules. Collapsed, a row is 107 tall -
// title at `h5`, a 105x59 thumbnail, two lines of description at `body` (16px, the file's,
// and the designer's call on 2026-09-26 over the 14 it was drawn at here), and a 48px
// circular button. Open, it is 302 - the thumbnail becomes a 254px square, growing
// leftwards from the same right edge. The file steps the open row's title up to `h4`;
// 2026-09-26 kept `h5` in both states, and 2026-11-03 restored the step (20 -> 26, the
// 14 -> 18 ratio asked for). Rows start closed, and the closed thumbnail wears a black 30%.
//
// `panels` (6377:88138): one 672px square photograph with a stack of panels beside it,
// the open one 448 tall and the closed ones 96, on a 16px gap.
//
// One item is open at a time, as the design shows, and the open one can be closed again -
// a disclosure that cannot be re-closed is a trap for anyone using a keyboard.
//
// The description is one field, clamped to two lines when collapsed rather than split
// into summary and detail. Two fields would be two things for an editor to keep in step,
// and the design's collapsed rows read as the opening of the same text.
//
// `links` (6377:87898, 2026-11-01): plain rows that do not open. A short name on the left
// (`title`), a heading and a paragraph beside it (`label`, `body`), and a grey 48px arrow at
// the right that links to `url`. The row itself is not a link - the arrow is, named by the
// row's title - and a row with no `url` draws no arrow rather than a dead one.
//
// The circular button is the exported `arrow-right` rotated, not a drawn glyph: the
// design's up-right and down-left arrows are the same arrow at -45 and 135 degrees.
import { useState } from 'react';
import { Eyebrow, SectionShell, withHighlight } from '../primitives';
import { Icon } from '../../components/ui/Icon';
import { Image, SIZES } from '../../components/ui/Image';
import { AppLink } from '../../components/ui/AppLink';
import type { Card, ServiceListSection } from '../../types/sections';

/** The 48px circle. Outlined and pointing up-right when closed, filled and down-left when open. */
function Toggle({ open }: { open: boolean }) {
  return (
    <span
      aria-hidden
      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition ${
        open ? 'bg-ink-strong text-white' : 'border border-grey-500 text-ink-strong'
      }`}
    >
      <Icon name="arrow-right" size={18} className={open ? 'rotate-[135deg]' : '-rotate-45'} />
    </span>
  );
}

/**
 * Which row is open.
 *
 * `alwaysOne` is the difference between the two layouts. In `rows` a reader can close
 * the open row and see the list on its own, which is what a disclosure should allow. In
 * `panels` the open one balances the photograph beside it, so closing the last one
 * leaves the column half the height of the picture and the section looks broken -
 * pressing the open panel there keeps it open, the way a tab set behaves.
 */
function useDisclosure(alwaysOne = false) {
  // Rows start closed, as designed; panels need one open to balance the photograph.
  const [open, setOpen] = useState<number | null>(alwaysOne ? 0 : null);
  return {
    open,
    toggle: (i: number) => setOpen((c) => (c === i && !alwaysOne ? null : i))
  };
}

const panelId = (name: string, i: number) => `${name}-panel-${i}`;
const buttonId = (name: string, i: number) => `${name}-button-${i}`;

/** Figma 6377:87365 - full-width rows, a thumbnail each. */
function Rows({ items, id }: { items: Card[]; id: string }) {
  const { open, toggle } = useDisclosure();

  return (
    <div className="lg:pl-[60px]">
      {items.map((item, i) => {
        const isOpen = open === i;
        return (
          <div key={i} className="border-t border-grey-400 last:border-b">
            <button
              type="button"
              id={buttonId(id, i)}
              aria-expanded={isOpen}
              aria-controls={panelId(id, i)}
              onClick={() => toggle(i)}
              className="flex w-full items-center gap-6 py-6 text-left"
            >
              <span className={`flex-1 text-ink-strong transition-[font-size] ${isOpen ? 'text-h4' : 'text-h5'}`}>
                {item.title}
              </span>

              {item.image ? (
                <span className={`relative hidden shrink-0 overflow-hidden rounded-xs bg-surface-sunken sm:block ${
                  isOpen ? 'h-[254px] w-[254px]' : 'h-[59px] w-[105px]'
                }`}>
                  <Image media={item.image} alt="" sizes="254px" className="h-full w-full object-cover" />
                  {/* Black at 30% while closed; the open thumbnail is shown as it is. */}
                  {isOpen ? null : <span aria-hidden className="absolute inset-0 bg-black/30" />}
                </span>
              ) : null}

              <span className="hidden w-[46%] shrink-0 lg:block">
                <span className={`block whitespace-pre-line text-body text-ink-muted ${isOpen ? '' : 'line-clamp-2'}`}>
                  {item.body}
                </span>
              </span>

              <Toggle open={isOpen} />
            </button>

            {/* The body again for the narrow layout, where it does not fit in the row. */}
            <div
              id={panelId(id, i)}
              role="region"
              aria-labelledby={buttonId(id, i)}
              hidden={!isOpen}
              className="pb-6 lg:hidden"
            >
              <p className="whitespace-pre-line text-body text-ink-muted">{item.body}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Figma 6377:87898 - rules, a name, a heading with its paragraph, an arrow. 1240 wide
 * inside the 1360 frame, so 60px in from each side; the name column is 424 and the text
 * 698 with 20 between, and each row has 32 above and below its rule.
 */
function LinkRows({ items }: { items: Card[] }) {
  return (
    <ul className="m-0 list-none border-b border-grey-500 p-0 lg:mx-[60px]">
      {items.map((item, i) => (
        <li
          key={i}
          className="flex flex-col gap-4 border-t border-grey-500 py-8 lg:flex-row lg:items-start lg:gap-[50px]"
        >
          <div className="flex min-w-0 flex-1 flex-col gap-4 lg:flex-row lg:gap-5">
            <p className="text-h5 text-ink-strong lg:w-[424px] lg:shrink-0">{item.title}</p>
            <div className="flex min-w-0 flex-1 flex-col gap-4">
              {item.label ? <p className="text-h5 text-ink-strong">{item.label}</p> : null}
              {item.body ? <p className="whitespace-pre-line text-body text-ink-muted">{item.body}</p> : null}
            </div>
          </div>

          {item.url ? (
            <AppLink
              to={item.url}
              aria-label={item.title}
              className="flex h-12 w-12 shrink-0 items-center justify-center self-end rounded-full bg-grey-700 text-white transition hover:bg-grey-900 lg:self-start"
            >
              <Icon name="arrow-right" size={24} />
            </AppLink>
          ) : <span aria-hidden className="hidden h-12 w-12 shrink-0 lg:block" />}
        </li>
      ))}
    </ul>
  );
}

/** Figma 6377:88138 - a square photograph beside a stack of panels. */
function Panels({ items, id, media, alt, claim }: {
  items: Card[]; id: string; media: ServiceListSection['media']; alt?: string; claim?: string;
}) {
  const { open, toggle } = useDisclosure(true);

  return (
    // `items-stretch` with the open panel growing keeps the two columns the same height,
    // which is the whole point of always leaving one open.
    <div className="grid items-stretch gap-4 lg:grid-cols-2">
      {media?.image ? (
        <figure className="aspect-square overflow-hidden rounded-m bg-surface-sunken">
          <Image media={media.image} alt={alt ?? ''} sizes={SIZES.half} className="h-full w-full object-cover" />
        </figure>
      ) : claim ? (
        // 3814:61446 - a filled blue card with one sentence, where other pages put a
        // photograph. It is a `p`, not a heading: the panels beside it carry the headings.
        <p className="flex items-start rounded-m bg-blue-dark-200 p-9 text-h4 text-white">{claim}</p>
      ) : null}

      <div className="flex flex-col gap-4">
        {items.map((item, i) => {
          const isOpen = open === i;
          return (
            <div key={i} className={`flex flex-col rounded-m bg-surface-muted ${isOpen ? 'flex-1' : 'shrink-0'}`}>
              <button
                type="button"
                id={buttonId(id, i)}
                aria-expanded={isOpen}
                aria-controls={panelId(id, i)}
                onClick={() => toggle(i)}
                className="flex w-full items-start justify-between gap-4 p-8 text-left"
              >
                <span className="text-h4 text-ink-strong">{item.title}</span>
                <Toggle open={isOpen} />
              </button>
              <div
                id={panelId(id, i)}
                role="region"
                aria-labelledby={buttonId(id, i)}
                hidden={!isOpen}
                className="px-8 pb-8"
              >
                <p className="whitespace-pre-line text-body-sm text-ink-muted">{item.body}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ServiceList({ data }: { data: ServiceListSection }) {
  const items = data.items ?? [];
  const id = `service-list-${data.id}`;

  return (
    <SectionShell settings={data.settings} name="service-list">
      {data.eyebrow ? <Eyebrow value={data.eyebrow} /> : null}

      {/* Two columns when there is a lead beside the heading, as Nasza firma has
          (3814:61442); the heading alone otherwise. */}
      {data.body ? (
        <div className={`grid gap-8 ${data.layout === 'links' ? 'lg:grid-cols-[785fr_535fr] lg:gap-10' : 'lg:grid-cols-2 lg:gap-16'}`}>
          <h2 className={`text-h2 text-ink ${data.layout === 'links' ? '' : 'lg:text-h1'}`}>{withHighlight(data.heading)}</h2>
          <p className="whitespace-pre-line text-body text-ink-muted">{data.body}</p>
        </div>
      ) : data.heading ? (
        // h3 over the file's 670 (6377:87367) - not the shared 46px `Heading`.
        <h2 className="max-w-[670px] text-h3 text-ink">{withHighlight(data.heading)}</h2>
      ) : null}

      {items.length ? (
        data.layout === 'links'
          ? <LinkRows items={items} />
          : data.layout === 'panels'
            ? <Panels items={items} id={id} media={data.media} alt={data.media?.alt} claim={data.claim ?? undefined} />
            : <Rows items={items} id={id} />
      ) : null}
    </SectionShell>
  );
}
